/**
 * Напоминания о сроке годности.
 *
 * Срок годности — единственное в аптечке, что меняется само и молча. Лекарство
 * лежит в коробке месяцами, и о том, что оно испортилось, человек узнаёт в
 * худший момент: когда оно понадобилось. Поэтому о сроке приложение
 * напоминает заранее — за месяц, когда замену ещё можно спокойно купить, — и
 * в день, когда принимать уже нельзя.
 *
 * Название лекарства в уведомление по умолчанию не попадает: оно приходит на
 * экран блокировки, а название — это диагноз, прочитанный вслух. Правило то же
 * самое, что у напоминаний о приёме, и настройка у них общая.
 */

import { effectiveExpiry, EXPIRY_SOON_DAYS } from '@/entities/medication/model/expiry';
import type { Medication } from '@/shared/db/schema';
import { addDays, formatDateFull, type IsoDate, MINUTES_IN_HOUR } from '@/shared/lib/datetime';
import { cancel, schedule } from '@/shared/notifications/local-notifications';

/**
 * Час напоминания о сроке.
 *
 * Не утро и не ночь: срок годности — дело неспешное, и будить им человека или
 * встречать его этим сразу после пробуждения незачем. В десять утра до аптеки
 * ещё можно дойти в тот же день.
 */
export const EXPIRY_REMINDER_MIN = 10 * MINUTES_IN_HOUR;

function soonId(medication: Medication): string {
  return `expiry-soon-${medication.id}`;
}

function expiredId(medication: Medication): string {
  return `expiry-${medication.id}`;
}

/** Текст напоминания о том, что срок на исходе. */
export function expirySoonText(
  medication: Medication,
  date: IsoDate,
  showName: boolean,
): { title: string; body: string } {
  if (!showName) {
    return {
      title: 'В аптечке кончается срок годности',
      body: 'Откройте аптечку, чтобы увидеть у чего',
    };
  }

  const name = [medication.name, medication.strength].filter(Boolean).join(' ');
  return { title: `Скоро истечёт: ${name}`, body: `Годен до ${formatDateFull(date)}` };
}

/** Текст напоминания о том, что срок вышел. */
export function expiredText(
  medication: Medication,
  showName: boolean,
): { title: string; body: string } {
  if (!showName) {
    return {
      title: 'В аптечке истёк срок годности',
      body: 'Откройте аптечку, чтобы увидеть у чего',
    };
  }

  const name = [medication.name, medication.strength].filter(Boolean).join(' ');
  return { title: `Срок вышел: ${name}`, body: 'Принимать нельзя — пора выбросить' };
}

/**
 * Приводит напоминания о сроках в соответствие с аптечкой.
 *
 * Два напоминания на лекарство: за месяц до конца срока и в первый день, когда
 * принимать уже нельзя. Оба заводятся с постоянными идентификаторами, поэтому
 * правка даты переносит напоминание, а не заводит второе; убранное из аптечки
 * или потерявшее срок лекарство своё напоминание снимает.
 */
export function syncExpiryReminders(
  medications: readonly Medication[],
  today: IsoDate,
  showNames = false,
): void {
  for (const medication of medications) {
    const date =
      medication.deletedAt === null && medication.archivedAt === null
        ? effectiveExpiry(medication)
        : null;

    if (date === null) {
      void cancel(soonId(medication));
      void cancel(expiredId(medication));
      continue;
    }

    const warnAt = addDays(date, -EXPIRY_SOON_DAYS);
    if (warnAt > today) {
      void schedule({
        id: soonId(medication),
        ...expirySoonText(medication, date, showNames),
        category: 'medication',
        date: warnAt,
        timeMin: EXPIRY_REMINDER_MIN,
        route: '/health',
      });
    } else {
      // Предупреждать «за месяц» задним числом нечем: этот день уже прошёл, и
      // об остатке срока говорит сама аптечка.
      void cancel(soonId(medication));
    }

    // День после срока — первый, когда принимать нельзя. В сам последний день
    // лекарство ещё годно, и «срок вышел» было бы неправдой.
    void schedule({
      id: expiredId(medication),
      ...expiredText(medication, showNames),
      category: 'medication',
      date: addDays(date, 1),
      timeMin: EXPIRY_REMINDER_MIN,
      route: '/health',
    });
  }
}
